import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress
} from '@mui/material';
import CryptoJS from "crypto-js";
import { KEY } from "../../dummyData"; // Adjust the import path as needed

const ScheduleDialog = ({
                            open,
                            onClose,
                            onSchedule,
                            staff = [],
                            selectedCount = 0,
                            selectedTicket = null,
                            selectedTickets = [],
                            ApiDetails,
                            showAlert
                        }) => {
    const [scheduleDate, setScheduleDate] = useState('');
    const [assignedStaffId, setAssignedStaffId] = useState('');
    const [scheduleNotes, setScheduleNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [availableStaff, setAvailableStaff] = useState(staff);

    // Fetch staff when component opens
    useEffect(() => {
        if (open) {
            // Only fetch staff if not already provided via props
            if (staff.length === 0) {
                fetchStaffList();
            } else {
                setAvailableStaff(staff);
            }
        }
    }, [open, staff]);

    // Get landlord ID from localStorage
    const getLandlordId = () => {
        try {
            let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("myMap"),
                KEY
            );
            let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText)[0][1];
        } catch (error) {
            console.error("Error getting landlord ID:", error);
            return null;
        }
    };

    // Fetch staff members
    const fetchStaffList = async () => {
        try {
            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert?.("Unable to get landlord ID", "error");
                return;
            }

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/staff", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

            if (response.status === 200) {
                const staffData = await response.json();
                setAvailableStaff(staffData);
            } else if (response.status !== 404) {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error fetching staff list:", error);
            showAlert?.("Failed to load staff members", "error");
        }
    };

    // Schedule maintenance
    const handleSchedule = async () => {
        if (!scheduleDate) return;

        try {
            setLoading(true);

            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert?.("Unable to get landlord ID", "error");
                return;
            }

            // Determine if it's a bulk action or single ticket
            const isBulk = selectedTickets && selectedTickets.length > 0;

            const scheduleData = {
                landlord_id: landlordId,
                staff_id: assignedStaffId,
                scheduled_start: scheduleDate,
                notes: scheduleNotes || ''
            };

            // Add the appropriate ticket id field
            if (isBulk) {
                scheduleData.ticket_ids = selectedTickets;
            } else if (selectedTicket) {
                scheduleData.issue_id = selectedTicket.id;
            } else {
                showAlert?.("No tickets selected for scheduling", "error");
                setLoading(false);
                return;
            }

            // Determine which endpoint to use
            const endpoint = isBulk ?
                ApiDetails + "pegasus/visionary/tickets/api/tickets/bulk/schedule" :
                ApiDetails + "pegasus/visionary/tickets/api/tickets/schedule";

            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify(scheduleData)
            });

            if (response.status === 200) {
                showAlert?.(
                    isBulk
                        ? `Scheduled ${selectedTickets.length} tickets successfully`
                        : "Maintenance scheduled successfully",
                    "success"
                );
                handleClose();
                onSchedule?.(scheduleDate, assignedStaffId);
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error scheduling maintenance:", error);
            showAlert?.("Failed to schedule maintenance", "error");
        } finally {
            setLoading(false);
        }
    };

    // Close dialog and reset state
    const handleClose = () => {
        setScheduleDate('');
        setAssignedStaffId('');
        setScheduleNotes('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ py: 2, px: 3 }}>
                Schedule Maintenance
            </DialogTitle>
            <DialogContent sx={{ px: 3, py: 2 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Schedule maintenance for {selectedCount > 0 ? selectedCount : 1} {selectedCount > 1 ? 'tickets' : 'ticket'}
                </Typography>
                <TextField
                    fullWidth
                    label="Schedule Date & Time"
                    type="datetime-local"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                    required
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="assign-staff-label">Assign To</InputLabel>
                    <Select
                        labelId="assign-staff-label"
                        label="Assign To"
                        value={assignedStaffId}
                        onChange={(e) => setAssignedStaffId(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>Keep current assignments</em>
                        </MenuItem>
                        {availableStaff.map(staffMember => (
                            <MenuItem key={staffMember.id} value={staffMember.id}>
                                {staffMember.name || `${staffMember.first_name} ${staffMember.last_name}`}
                                {staffMember.role && ` (${staffMember.role})`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    fullWidth
                    label="Notes (Optional)"
                    multiline
                    rows={2}
                    value={scheduleNotes}
                    onChange={(e) => setScheduleNotes(e.target.value)}
                    sx={{ mb: 2 }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={handleClose}
                    sx={{ borderRadius: '8px' }}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSchedule}
                    disabled={!scheduleDate || loading}
                    sx={{ borderRadius: '8px' }}
                >
                    {loading ? <CircularProgress size={24} /> : "Schedule"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleDialog;